// Луч следа под отметкой «судно» на оптическом снимке.
//
// ЗАЧЕМ. Вердикт «судно» на оптике держится ЦЕЛИКОМ на кильватерном следе:
// яркую компактную точку дают и блик, и мелкое облако, а полосы за собой они не
// оставляют (см. `src/imagery/detect/wake.py`). Не показав след, карточка
// просит верить на слово — и первая же отметка на проде вызвала у владельца
// «сейчас ощущение, что это облако», проверить которое было нечем.
//
// ЛУЧ — ЭТО ПРОБА, А НЕ ИЗМЕРЕННЫЙ СЛЕД, и путать нельзя. Прибор считает
// МЕДИАНУ яркости вдоль луча фиксированной длины (3 км) и берёт направление, на
// котором она выше; докуда след тянется НА САМОМ ДЕЛЕ, он не ищет. Значит
// дальний far линии — это «докуда смотрели», и выдать его за длину следа
// значило бы сообщить измерение, которого никто не делал. Отсюда пометка
// `wake_probe` на фиче: подпись обязана сказать это словами.
//
// СЛЕД — КОНУС, А НЕ ЛИНИЯ (замечание владельца 05.09.2026: «след имеет форму
// не одной линии, а двух, образующих угол в точке пересечения, а точка
// пересечения — это корабль»). Замер по подтверждённому АИС газовозу GASLOG
// GENOA: на кольце 800 м вдоль оси стоят ДВА пика — 38–39° и 51–52°, между ними
// провал, сразу за ними тёмная полоса. Раствор при этом ИЗМЕРЯЕТСЯ прибором
// (`wake_cone_deg`), а не берётся каноническим кельвиновским 39°: тот описывает
// гребни на глубокой воде, мы же видим пенный след, и на 10 м/пиксель его
// кромки лежат втрое ближе. Отметке без замера раствора рисуется одна линия —
// конус наугад сообщил бы форму, которой никто не мерил.

import { M_PER_NM, runTo } from '../map/rhumb'

/** Метка на фиче: линия — проба фиксированной длины, а не измеренный след. */
export const WAKE_PROBE_KEY = 'wake_probe'

export interface WakeMark {
  lon: number
  lat: number
  course_deg: number | null
  evidence?: Record<string, number | boolean | null | undefined> | null
}

export interface WakeRayOptions {
  /** Долгота центра кадра — приводит линию в тот оборот мира, в котором СЕЙЧАС
   *  стоит карта. В базе долгота знаковая (−179,9°), и за швом линия уехала бы
   *  на целый мир левее экрана. Тот же приём, что у прогнозного хода судна. */
  centerLng?: number
}

export interface WakeRayFeature {
  type: 'Feature'
  geometry:
    | { type: 'LineString'; coordinates: [number, number][] }
    | { type: 'MultiLineString'; coordinates: [number, number][][] }
  properties: Record<string, number | boolean | string | null>
}

/** Ниже этого раствора конус неотличим от линии, и вторая кромка — украшение. */
export const MIN_CONE_DEG = 1

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/** В какую сторону тянется след, ° от севера по часовой. `null` — не знаем.
 *
 *  Азимут из улик СИЛЬНЕЕ выведенного из курса: курс — производная от него
 *  (обратный bearing), и, выводя его обратно, мы дважды округлили бы одно asNumber.
 *  Старые отметки bearingа не несут — для них обратный ход единственный. */
function bearingOf(mark: WakeMark): number | null {
  const measured = asNumber(mark.evidence?.wake_bearing_deg)
  if (measured != null) return ((measured % 360) + 360) % 360
  const course = asNumber(mark.course_deg)
  if (course == null) return null
  return ((course + 180) % 360 + 360) % 360
}

/** Приводит долготу в оборот мира кадра. Без центра — как есть. */
function inFrame(lon: number, centerLng?: number): number {
  if (centerLng == null) return lon
  return lon + Math.round((centerLng - lon) / 360) * 360
}

/**
 * Линия следа от отметки за корму. `null` — рисовать нечего, и это ОТВЕТ.
 *
 * Луча нет в трёх случаях, и каждый значит своё: следа не нашли (мерили и не
 * нашли), направление неизвестно (луч «на север» был бы выдумкой), probeM пробы
 * нулевая (мерить было негде — край кадра). Нарисованный вместо любого из них
 * луч утверждал бы улику, которой нет.
 */
export function wakeRay(mark: WakeMark, opts: WakeRayOptions = {}): WakeRayFeature | null {
  if (mark.evidence?.has_wake !== true) return null

  // РАДАР ДАЁТ ОСЬ, А НЕ НАПРАВЛЕНИЕ, и ветка стоит первой, потому что у такой
  // отметки нет ни курса, ни азимута следа — выводить их не из чего. Движущаяся
  // цель смещена вдоль азимутальной оси съёмки относительно собственной полосы,
  // и судно оказывается то позади неё, то впереди: выбор стороны был бы
  // подбрасыванием монеты. Поэтому линия идёт в ОБЕ стороны от отметки, а
  // раствора конуса у неё нет — его мерит только оптическая проба.
  const axis = asNumber(mark.evidence?.wake_axis_deg)
  if (axis != null) {
    const probe = asNumber(mark.evidence?.wake_length_m)
    if (probe == null || probe <= 0) return null
    const lonA = inFrame(mark.lon, opts.centerLng)
    const half = probe / M_PER_NM

    // ЛИНИЯ СТОИТ ТАМ, ГДЕ ЕЁ НАМЕРИЛИ. Синтезированная апертура сносит
    // движущуюся цель вдоль азимутальной оси съёмки, и след лежит в сотнях
    // метров сбоку от отметки (замер 05.09.2026: медиана 320 м). Проведя
    // линию сквозь судно, мы поставили бы её туда, где на снимке ничего нет,
    // — и человек, пришедший проверить улику глазом, её не нашёл бы.
    const offsetM = asNumber(mark.evidence?.wake_offset_m) ?? 0
    const [lonC, latC] = offsetM === 0
      ? [lonA, mark.lat]
      : runTo(mark.lat, lonA, axis + 90, offsetM / M_PER_NM)

    const edge = (d: number): [number, number][] => [
      [lonC, latC], runTo(latC, lonC, axis + d, half),
    ]
    return {
      type: 'Feature',
      geometry: { type: 'MultiLineString', coordinates: [edge(0), edge(180)] },
      properties: {
        [WAKE_PROBE_KEY]: true,
        wake_axis_deg: ((axis % 180) + 180) % 180,
        wake_length_m: probe,
        wake_offset_m: offsetM,
        wake_strength_sigma: asNumber(mark.evidence?.wake_strength_sigma),
      },
    }
  }

  const bearing = bearingOf(mark)
  if (bearing == null) return null
  const probeM = asNumber(mark.evidence?.wake_length_m)
  if (probeM == null || probeM <= 0) return null

  const lon0 = inFrame(mark.lon, opts.centerLng)
  const miles = probeM / M_PER_NM
  const cone = asNumber(mark.evidence?.wake_cone_deg)
  const properties = {
    [WAKE_PROBE_KEY]: true,
    wake_bearing_deg: bearing,
    wake_length_m: probeM,
    wake_cone_deg: cone,
    wake_strength: asNumber(mark.evidence?.wake_strength),
  }
  if (cone != null && cone >= MIN_CONE_DEG) {
    // Обе кромки выходят ИЗ ОТМЕТКИ: точка их пересечения и есть судно.
    const edge = (d: number) => [[lon0, mark.lat] as [number, number],
                                 runTo(mark.lat, lon0, bearing + d, miles)]
    return {
      type: 'Feature',
      geometry: { type: 'MultiLineString', coordinates: [edge(-cone / 2), edge(cone / 2)] },
      properties,
    }
  }
  return {
    type: 'Feature',
    geometry: { type: 'LineString', coordinates: [[lon0, mark.lat],
                                                  runTo(mark.lat, lon0, bearing, miles)] },
    properties,
  }
}

export function wakeCollection(marks: WakeMark[], opts: WakeRayOptions = {}) {
  const features: WakeRayFeature[] = []
  for (const m of marks) {
    const ray = wakeRay(m, opts)
    if (ray) features.push(ray)
  }
  return { type: 'FeatureCollection' as const, features }
}
